import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import PageHeader from '@/shared/components/PageHeader';
import { Card, Select, Space } from '@/shared/ui/primitives';
import { aiAdApi, type WhatsAppSuggestionStatus } from '../api/aiAds.api';
import WhatsAppUpdateSuggestionsPanel from '../components/WhatsAppUpdateSuggestionsPanel';

const STATUS_OPTIONS: Array<{ value: WhatsAppSuggestionStatus; label: string }> = [
  { value: 'PENDING', label: 'معلقة' },
  { value: 'APPLIED', label: 'تم اعتمادها' },
  { value: 'IGNORED', label: 'تم تجاهلها' },
  { value: 'UNMATCHED', label: 'غير مرتبطة' },
];

export default function AiAdWhatsAppUpdatesPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<WhatsAppSuggestionStatus>('PENDING');

  const query = useQuery({
    queryKey: ['ai-ads', 'whatsapp-update-suggestions', status],
    queryFn: () => aiAdApi.listWhatsAppUpdateSuggestions({ status, size: 50 }),
    refetchInterval: status === 'PENDING' ? 30000 : undefined,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ai-ads', 'whatsapp-update-suggestions'] });

  const applyMutation = useMutation({
    mutationFn: (id: number) => aiAdApi.applyWhatsAppUpdateSuggestion(id),
    onSuccess: () => {
      toast.success('تم اعتماد التحديث');
      refresh();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'تعذر اعتماد التحديث'),
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: number) => aiAdApi.ignoreWhatsAppUpdateSuggestion(id),
    onSuccess: () => {
      toast.success('تم تجاهل الرد');
      refresh();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'تعذر تجاهل الرد'),
  });

  return (
    <div>
      <PageHeader
        title="ردود واتساب على إعلانات الذكاء الاصطناعي"
        subtitle="راجع رد العميل، وشاهد القيم قبل وبعد، ثم قرر تحديث الإعلان أو تجاهل الرد."
      />
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>الحالة</span>
          <Select value={status} onChange={(value: any) => setStatus(value)} options={STATUS_OPTIONS} style={{ minWidth: 180 }} />
        </Space>
      </Card>
      <WhatsAppUpdateSuggestionsPanel
        suggestions={query.data?.content ?? []}
        loading={query.isLoading}
        onRefresh={refresh}
        onApply={(id) => applyMutation.mutate(id)}
        onIgnore={(id) => ignoreMutation.mutate(id)}
        busy={applyMutation.isPending || ignoreMutation.isPending}
      />
    </div>
  );
}
